import { useTranslation } from 'react-i18next';
import { resolveLocalizedName, type PickupHandoffModeV3, type SessionMetadataCollect } from 'pi-kiosk-shared';
import { PickupSlotPicker } from '../PickupSlotPicker.js';
import type { CollectStepSheetProps } from './collectStepSheetTypes.js';
import {
  isSelfPickupHandoffMode,
  isRetailHandoffChoiceSelected,
  resolveRetailHandoffChoices,
} from './collectHandoffChoices.js';
import { SelfCollectConfirmToggle } from './SelfCollectConfirmToggle.js';
import {
  resolveCollectSheetDisplayTiming,
  shouldShowNowTimingUnderLaterLock,
} from '../../shop/presenters/shopCheckoutPresenter.js';
import {
  isSoleHandoffAnnouncement,
  isSolePointWithoutHandoffAnnouncement,
  isSoleTimingAnnouncement,
  isSoleCollectOptions,
  resolvePickupCollectTitleKey,
  resolvePickupCollectIntroKey,
} from './collectSoleOptionPresentation.js';
import {
  isPickupPointOpenNow,
  resolveModeSOpenGatePoint,
} from '../pickupPointIsOpenNow.js';

type RetailCollectProps = Omit<CollectStepSheetProps, 'usePickupCopyKeys'> & {
  readonly hideHeader?: boolean;
};

function isSelfHandoff(mode: PickupHandoffModeV3): boolean {
  return isSelfPickupHandoffMode(mode);
}

function SelfPickupIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
      <circle cx="12" cy="8" r="3" />
      <path d="M6 20c1.2-3 3.4-4.5 6-4.5s4.8 1.5 6 4.5" />
      <path d="M9 12h6l1 4H8l1-4z" />
    </svg>
  );
}

function BarPickupIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
      <path d="M4 10h16v2H4z" />
      <path d="M6 12v8M18 12v8" />
      <path d="M8 6h8v4H8z" />
    </svg>
  );
}

function ClockIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l3 2" />
    </svg>
  );
}

function PlaceIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
      <path d="M12 21s7-4.5 7-11a7 7 0 10-14 0c0 6.5 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

function CheckBadgeIcon(): JSX.Element {
  return (
    <span
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent)] text-[var(--color-accent-foreground)]"
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M5 12l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function ChevronDownIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-[var(--color-on-surface-muted)]" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface PickupChoiceCardProps {
  readonly selected: boolean;
  readonly icon: JSX.Element;
  readonly title: string;
  readonly subtitle: string;
  readonly onSelect: () => void;
  readonly testId?: string;
}

function PickupChoiceCard({
  selected,
  icon,
  title,
  subtitle,
  onSelect,
  testId,
}: PickupChoiceCardProps): JSX.Element {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onSelect}
      className={[
        'flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-colors',
        selected
          ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/5'
          : 'border-[var(--color-border)] bg-[var(--color-surface)] hover:bg-[var(--color-surface-elevated)]',
      ].join(' ')}
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--color-surface-muted)] text-[var(--color-on-surface)]">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-[var(--color-on-surface)]">{title}</span>
        <span className="mt-0.5 block text-xs text-[var(--color-on-surface-muted)]">{subtitle}</span>
      </span>
      {selected ? <CheckBadgeIcon /> : null}
    </button>
  );
}

interface PickupAnnouncementCardProps {
  readonly icon: JSX.Element;
  readonly eyebrow: string;
  readonly title: string;
  readonly subtitle: string;
  readonly testId?: string;
}

/** Non-interactive sole-option announcement — avoids implying more choices exist. */
function PickupAnnouncementCard({
  icon,
  eyebrow,
  title,
  subtitle,
  testId,
}: PickupAnnouncementCardProps): JSX.Element {
  return (
    <div
      role="status"
      data-testid={testId}
      className="flex w-full items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-left"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--color-surface-muted)] text-[var(--color-on-surface)]">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-medium text-[var(--color-on-surface-muted)]">{eyebrow}</span>
        <span className="mt-0.5 block text-sm font-semibold text-[var(--color-on-surface)]">{title}</span>
        {subtitle.length > 0 ? (
          <span className="mt-0.5 block text-xs text-[var(--color-on-surface-muted)]">{subtitle}</span>
        ) : null}
      </span>
    </div>
  );
}

function resolveSelfHandoffSubtitle(params: {
  readonly t: (key: string) => string;
  readonly timing: SessionMetadataCollect['timing'];
}): string {
  // Immediate copy only for NOW — sole LATER must not claim ready-right-after-payment.
  if (params.timing === 'NOW') {
    return params.t('pickup.handoff.selfHelperImmediate');
  }
  return params.t('pickup.handoff.selfHelper');
}

export function CollectPickupSheetRetail({
  kioskId,
  points,
  pointsLoading,
  pointsLoadError = false,
  onPointsRetry,
  collect,
  scheduledPickupEnabled,
  opsMode = 'NORMAL',
  pickupSlot,
  onPickupSlotChange,
  onCollectChange,
  lockedCollectTiming,
  allowedPickupHandoffModes = [],
  selectedPickupHandoffMode = null,
  onPickupHandoffModeChange,
  selfCollectOnlyGate = false,
  selfCollectConfirmed = false,
  onSelfCollectConfirmedChange,
  selfCollectConfirmAtKiosk = false,
  payOnSpotProximityNoticeMinutes,
  hideHeader = false,
  salesPointName = null,
  salesPointLocation = null,
}: RetailCollectProps): JSX.Element {
  const { t, i18n } = useTranslation('customer');
  const selfCollectLabel = selfCollectConfirmAtKiosk
    ? t('pickup.selfCollectConfirm.label')
    : t('pickup.selfCollectConfirm.onSiteLabel');
  const selfCollectHelper = selfCollectConfirmAtKiosk
    ? t('pickup.selfCollectConfirm.helper')
    : t('pickup.selfCollectConfirm.onSiteHelper');
  const timing = resolveCollectSheetDisplayTiming({ lockedCollectTiming, collect });
  const selectedPointId = collect?.pickupPointId ?? null;
  const handoffChoices = resolveRetailHandoffChoices(allowedPickupHandoffModes);
  const showHandoffPicker =
    handoffChoices.length > 1 && onPickupHandoffModeChange !== undefined;
  const effectiveHandoffMode =
    selectedPickupHandoffMode ??
    (allowedPickupHandoffModes.length === 1 ? allowedPickupHandoffModes[0] : null);
  const selectedPoint = points.find((point) => point.id === selectedPointId) ?? null;
  const defaultPoint = points.find((point) => point.isOnSpot) ?? points[0];
  const placePoint = selectedPoint ?? defaultPoint ?? null;
  const placePointName =
    placePoint != null
      ? resolveLocalizedName(placePoint.name, placePoint.nameLocales, i18n.language)
      : '';
  const trimmedSalesPointName = salesPointName?.trim() ?? '';
  const trimmedSalesPointLocation = salesPointLocation?.trim() ?? '';
  const displayPlaceName =
    trimmedSalesPointName.length > 0 ? trimmedSalesPointName : placePointName;
  const displayPlaceLocation = trimmedSalesPointLocation;
  const barHandoffSubtitle =
    placePoint != null
      ? resolveLocalizedName(placePoint.name, placePoint.nameLocales, i18n.language)
      : '';
  const preserveUsableNow = shouldShowNowTimingUnderLaterLock(lockedCollectTiming, collect);
  const modeSGatePoint = resolveModeSOpenGatePoint(points, selectedPointId);
  const modeSIsOpenNow = isPickupPointOpenNow({
    isOpenNow: modeSGatePoint?.isOpenNow,
    openHoursJson: modeSGatePoint?.openHoursJson,
  });
  /** Mode S NOW offer — gated on isOpenNow (Spec Mode S lock). Never during PAUSE. */
  const showNowTiming =
    opsMode !== 'PAUSE' &&
    modeSIsOpenNow &&
    (lockedCollectTiming === undefined || lockedCollectTiming === 'NOW' || preserveUsableNow);
  const showLaterTiming =
    opsMode !== 'PAUSE' &&
    !preserveUsableNow &&
    (lockedCollectTiming === undefined || lockedCollectTiming === 'LATER') &&
    scheduledPickupEnabled;
  const timingLocked = lockedCollectTiming !== undefined && !preserveUsableNow;
  /** Mode S must never mount slot calendar — LATER-only (Spec Mode S lock). PAUSE hides calendar. */
  const showModeLSlotCalendar =
    opsMode !== 'PAUSE' &&
    timing === 'LATER' &&
    scheduledPickupEnabled &&
    selectedPointId !== null;
  const opsPaused = opsMode === 'PAUSE';
  const opsDelayed = opsMode === 'DELAY';
  const soleInput = {
    handoffChoiceCount: handoffChoices.length,
    showNowTiming,
    showLaterTiming,
    pickupPointCount: points.length,
  };
  const soleHandoffAnnouncement = isSoleHandoffAnnouncement(handoffChoices.length);
  const solePointWithoutHandoff = isSolePointWithoutHandoffAnnouncement(
    handoffChoices.length,
    points.length,
  );
  const soleTimingAnnouncement = isSoleTimingAnnouncement({ showNowTiming, showLaterTiming });
  const soleCollectOptions = isSoleCollectOptions(soleInput);
  const titleKey = resolvePickupCollectTitleKey(soleInput);
  const introKey = resolvePickupCollectIntroKey(soleInput);
  const showPlaceAnnouncement =
    displayPlaceName.length > 0 ||
    displayPlaceLocation.length > 0 ||
    soleCollectOptions ||
    soleHandoffAnnouncement ||
    soleTimingAnnouncement ||
    solePointWithoutHandoff;
  const placeLocationLine =
    displayPlaceLocation.length > 0
      ? displayPlaceLocation
      : t('pickup.placeLocationMissing');

  const selectPoint = (pickupPointId: number): void => {
    // G2 — emit display timing (usable NOW under LATER lock), never slot-less LATER.
    onCollectChange({
      timing,
      pickupPointId,
      ...(timing === 'LATER' && collect?.pickup !== undefined ? { pickup: collect.pickup } : {}),
    });
  };

  const selectTiming = (nextTiming: SessionMetadataCollect['timing']): void => {
    if (
      lockedCollectTiming !== undefined &&
      nextTiming !== lockedCollectTiming &&
      !(preserveUsableNow && nextTiming === 'NOW')
    ) {
      return;
    }
    // Mode S closed → refuse NOW selection (offer gate).
    if (nextTiming === 'NOW' && !modeSIsOpenNow) {
      return;
    }
    const pointId = selectedPointId ?? defaultPoint?.id;
    if (pointId === undefined) {
      return;
    }
    onCollectChange({
      timing: nextTiming,
      pickupPointId: pointId,
      ...(nextTiming === 'LATER' && pickupSlot
        ? {
            pickup: {
              promisedPickupAt: pickupSlot.startAt,
              pickupWindowEndAt: pickupSlot.endAt,
            },
          }
        : {}),
    });
    if (nextTiming === 'NOW') {
      // Mode S — clear any residual slot; never keep calendar state on NOW.
      onPickupSlotChange(null);
    }
  };

  const selectHandoff = (mode: PickupHandoffModeV3): void => {
    onPickupHandoffModeChange?.(mode);
    const preferredPoint = isSelfHandoff(mode)
      ? (points.find((point) => point.isOnSpot) ?? defaultPoint)
      : (points.find((point) => !point.isOnSpot) ?? selectedPoint ?? defaultPoint);
    if (preferredPoint !== undefined) {
      selectPoint(preferredPoint.id);
    }
  };

  const showBarPointPicker =
    showHandoffPicker &&
    effectiveHandoffMode != null &&
    !isSelfHandoff(effectiveHandoffMode) &&
    points.length > 1;

  if (pointsLoading) {
    return (
      <section data-testid="collect-step-sheet">
        <p className="text-sm text-[var(--color-on-surface-muted)]">{t('checkout.collectStep.loading')}</p>
      </section>
    );
  }

  // G1/G7 — Prefer selectable points (incl. SP on-spot fallback) over loadError/noPoints.
  if (points.length === 0) {
    if (pointsLoadError) {
      return (
        <section data-testid="collect-step-sheet" className="flex flex-col gap-3">
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {t('checkout.collectStep.loadError')}
          </p>
          {onPointsRetry !== undefined ? (
            <button
              type="button"
              className="self-start text-sm font-medium text-[var(--color-accent)] underline underline-offset-2"
              data-testid="collect-step-points-retry"
              onClick={onPointsRetry}
            >
              {t('shared.retry')}
            </button>
          ) : null}
        </section>
      );
    }
    return (
      <section data-testid="collect-step-sheet">
        <p className="text-sm text-[var(--color-danger)]" role="alert">
          {t('checkout.collectStep.noPoints')}
        </p>
      </section>
    );
  }

  const showReadyNotice =
    payOnSpotProximityNoticeMinutes !== undefined && payOnSpotProximityNoticeMinutes > 0;

  let handoffFieldsetContent: JSX.Element;
  if (showHandoffPicker) {
    handoffFieldsetContent = (
      <>
        {handoffChoices.map((choice) => {
          const self = choice.kind === 'self';
          return (
            <PickupChoiceCard
              key={choice.key}
              selected={isRetailHandoffChoiceSelected(choice, effectiveHandoffMode ?? null)}
              icon={self ? <SelfPickupIcon /> : <BarPickupIcon />}
              title={t(self ? 'pickup.handoff.self' : 'pickup.handoff.bar')}
              subtitle={
                self
                  ? resolveSelfHandoffSubtitle({
                      t,
                      timing,
                    })
                  : barHandoffSubtitle
              }
              onSelect={() => selectHandoff(choice.mode)}
              testId={self ? 'customer-pickup-handoff-self' : 'customer-pickup-handoff-bar'}
            />
          );
        })}
      </>
    );
  } else if (soleHandoffAnnouncement) {
    const only = handoffChoices[0];
    const self = only?.kind === 'self';
    handoffFieldsetContent = (
      <PickupAnnouncementCard
        icon={self === true ? <SelfPickupIcon /> : <BarPickupIcon />}
        eyebrow={t('pickup.onlyHandoffLabel')}
        title={t(self === true ? 'pickup.handoff.self' : 'pickup.handoff.bar')}
        subtitle={
          self === true
            ? resolveSelfHandoffSubtitle({
                t,
                timing,
              })
            : barHandoffSubtitle
        }
        testId={self === true ? 'customer-pickup-handoff-self' : 'customer-pickup-handoff-bar'}
      />
    );
  } else if (solePointWithoutHandoff) {
    const solePoint = points[0];
    const solePointName =
      solePoint != null
        ? resolveLocalizedName(solePoint.name, solePoint.nameLocales, i18n.language)
        : '';
    handoffFieldsetContent = (
      <PickupAnnouncementCard
        icon={<BarPickupIcon />}
        eyebrow={t('pickup.onlyPointLabel')}
        title={solePointName}
        subtitle={t('pickup.handoff.bar')}
        testId="customer-pickup-point-card"
      />
    );
  } else {
    handoffFieldsetContent = (
      <>
        {points.map((point) => (
          <PickupChoiceCard
            key={point.id}
            selected={selectedPointId === point.id}
            icon={<BarPickupIcon />}
            title={t('pickup.handoff.bar')}
            subtitle={resolveLocalizedName(point.name, point.nameLocales, i18n.language)}
            onSelect={() => selectPoint(point.id)}
            testId="customer-pickup-point-card"
          />
        ))}
      </>
    );
  }

  return (
    <section data-testid="collect-step-sheet" className="flex w-full min-w-0 flex-col gap-5">
      {onPointsRetry !== undefined ? (
        <button
          type="button"
          className="self-start text-xs font-medium text-[var(--color-on-surface-muted)] underline underline-offset-2"
          data-testid="collect-step-points-retry"
          onClick={onPointsRetry}
        >
          {t('shared.retry')}
        </button>
      ) : null}
      {hideHeader ? null : (
        <div>
          <p className="text-base font-semibold text-[var(--color-on-surface)]">
            {t(titleKey)}
          </p>
          <p className="mt-1 text-sm text-[var(--color-on-surface-muted)]">
            {t(introKey)}
          </p>
        </div>
      )}

      {showPlaceAnnouncement ? (
        <div
          className="flex w-full items-start gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
          data-testid="customer-pickup-place"
          role="status"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--color-surface-muted)] text-[var(--color-on-surface)]">
            <PlaceIcon />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-medium text-[var(--color-on-surface-muted)]">
              {t('pickup.placeLegend')}
            </span>
            {displayPlaceName.length > 0 ? (
              <span
                className="mt-0.5 block text-sm font-semibold text-[var(--color-on-surface)]"
                data-testid="customer-pickup-place-name"
              >
                {displayPlaceName}
              </span>
            ) : null}
            <span
              className="mt-0.5 block text-xs text-[var(--color-on-surface-muted)]"
              data-testid="customer-pickup-place-location"
            >
              {placeLocationLine}
            </span>
          </span>
        </div>
      ) : null}

      <div className="flex flex-col gap-2" data-testid="collect-handoff-fieldset">
        {handoffFieldsetContent}
      </div>

      {showBarPointPicker ? (
        <div className="flex flex-col gap-2" data-testid="collect-point-fieldset">
          <p className="text-sm font-medium text-[var(--color-on-surface)]">{t('pickup.pointLegend')}</p>
          {points.map((point) => (
            <PickupChoiceCard
              key={point.id}
              selected={selectedPointId === point.id}
              icon={<BarPickupIcon />}
              title={resolveLocalizedName(point.name, point.nameLocales, i18n.language)}
              subtitle={point.isOnSpot ? t('checkout.collectStep.onSpot') : t('pickup.handoff.bar')}
              onSelect={() => selectPoint(point.id)}
            />
          ))}
        </div>
      ) : null}

      {!modeSIsOpenNow && !showLaterTiming && !opsPaused ? (
        <div
          className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3"
          role="alert"
          data-testid="customer-pickup-closed-now"
        >
          <p className="text-sm font-medium text-[var(--color-on-surface)]">
            {t('pickup.closedNowTitle')}
          </p>
          <p className="mt-1 text-xs text-[var(--color-on-surface-muted)]">
            {t('pickup.closedNowBody')}
          </p>
        </div>
      ) : null}

      {opsPaused ? (
        <div
          className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3"
          role="alert"
          data-testid="customer-pickup-ops-paused"
        >
          <p className="text-sm font-medium text-[var(--color-on-surface)]">
            {t('pickup.opsPausedTitle')}
          </p>
          <p className="mt-1 text-xs text-[var(--color-on-surface-muted)]">
            {t('pickup.opsPausedBody')}
          </p>
        </div>
      ) : null}

      {opsDelayed && !opsPaused ? (
        <div
          className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3"
          role="status"
          data-testid="customer-pickup-ops-delayed"
        >
          <p className="text-sm text-[var(--color-on-surface-muted)]">
            {t('pickup.opsDelayedBody')}
          </p>
        </div>
      ) : null}

      {showNowTiming || showLaterTiming ? (
        <div className="flex flex-col gap-2" data-testid="collect-timing-fieldset">
          {soleTimingAnnouncement && showNowTiming ? (
            <div
              role="status"
              data-testid="customer-pickup-timing-asap"
              className="flex w-full items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-left"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium text-[var(--color-on-surface-muted)]">
                  {t('pickup.onlyTimingLabel')}
                </span>
                <span className="mt-0.5 block text-sm font-medium text-[var(--color-on-surface)]">
                  {t('pickup.timingAsap')}
                </span>
              </span>
            </div>
          ) : null}
          {soleTimingAnnouncement && showLaterTiming ? (
            <div
              role="status"
              data-testid="customer-pickup-timing-later"
              className="flex w-full items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-left"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium text-[var(--color-on-surface-muted)]">
                  {t('pickup.onlyTimingLabel')}
                </span>
                <span className="mt-0.5 block text-sm font-medium text-[var(--color-on-surface)]">
                  {t('checkout.collectStep.timingLater')}
                </span>
              </span>
            </div>
          ) : null}
          {!soleTimingAnnouncement ? (
            <>
              <p className="text-sm font-medium text-[var(--color-on-surface)]">{t('pickup.timeSelect')}</p>
              {showNowTiming ? (
                <button
                  type="button"
                  disabled={timingLocked ? timing !== 'NOW' : false}
                  onClick={() => selectTiming('NOW')}
                  className={[
                    'flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left',
                    timing === 'NOW'
                      ? 'border-[var(--color-accent)] bg-[var(--color-surface)]'
                      : 'border-[var(--color-border)] bg-[var(--color-surface)]',
                  ].join(' ')}
                  data-testid="customer-pickup-timing-asap"
                >
                  <span className="text-sm font-medium text-[var(--color-on-surface)]">
                    {t('pickup.timingAsap')}
                  </span>
                  <ChevronDownIcon />
                </button>
              ) : null}
              {showLaterTiming ? (
                <button
                  type="button"
                  disabled={timingLocked ? timing !== 'LATER' : false}
                  onClick={() => selectTiming('LATER')}
                  className={[
                    'flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left',
                    timing === 'LATER'
                      ? 'border-[var(--color-accent)] bg-[var(--color-surface)]'
                      : 'border-[var(--color-border)] bg-[var(--color-surface)]',
                  ].join(' ')}
                  data-testid="customer-pickup-timing-later"
                >
                  <span className="text-sm font-medium text-[var(--color-on-surface)]">
                    {t('checkout.collectStep.timingLater')}
                  </span>
                  <ChevronDownIcon />
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {showModeLSlotCalendar && selectedPointId !== null ? (
        <div className="flex w-full min-w-0 flex-col gap-3" data-testid="scheduled-pickup-section">
          <PickupSlotPicker
            kioskId={kioskId}
            pickupPointId={selectedPointId}
            value={pickupSlot}
            onChange={(slot) => {
              onPickupSlotChange(slot);
              if (slot !== null) {
                onCollectChange({
                  timing: 'LATER',
                  pickupPointId: selectedPointId,
                  pickup: {
                    promisedPickupAt: slot.startAt,
                    pickupWindowEndAt: slot.endAt,
                  },
                });
                return;
              }
              // FIX 4.1: day mismatch clears slot → omit pickup so Continue stays gated
              onCollectChange({
                timing: 'LATER',
                pickupPointId: selectedPointId,
              });
            }}
          />
        </div>
      ) : null}

      {showReadyNotice && timing === 'NOW' ? (
        <div
          className="flex items-start gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3"
          role="status"
          data-testid="customer-pickup-ready-estimate"
        >
          <span className="mt-0.5 text-[var(--color-on-surface-muted)]">
            <ClockIcon />
          </span>
          <span className="text-sm text-[var(--color-on-surface)]">{t('pickup.readyTitle')}</span>
        </div>
      ) : null}

      {selfCollectOnlyGate && onSelfCollectConfirmedChange !== undefined ? (
        <SelfCollectConfirmToggle
          label={selfCollectLabel}
          helper={selfCollectHelper}
          checked={selfCollectConfirmed}
          onCheckedChange={onSelfCollectConfirmedChange}
          className="flex items-start justify-between gap-3 rounded-xl border border-[var(--color-border)] px-4 py-3"
        />
      ) : null}
    </section>
  );
}
